/**
 * Колбэки **открытого** DocEditor (один активный сеанс на вкладку).
 * Дополняет `onWsCallback`: уведомляет UI редактора о WS save/error.
 *
 * @see dev/WUI-316.md §4.3
 */
import {
  isOfficeCallbackSaveReady,
  isOfficeCallbackTerminalError,
  type OfficeCallbackStatusEvent,
} from '@/services/r7AttachmentEditor.service'

/** Обработчики, регистрируемые в `useAttachmentR7Editor.openAttachment`. */
export type R7EditorOfficeCallbackHandlers = {
  elementId: number
  documentSessionId: string
  documentKey: string | null
  onEditorEvent?: (name: string, payload: unknown) => void
  onStaged?: (result: { storageUri: string; changed: boolean }) => void
  onSaveError?: (fileName: string) => void
}

let activeSession: R7EditorOfficeCallbackHandlers | null = null

function matchesActiveSession(event: OfficeCallbackStatusEvent): boolean {
  if (!activeSession) return false
  const sessionId = activeSession.documentSessionId.trim()
  if (sessionId && event.documentSessionId === sessionId) return true
  const documentKey = activeSession.documentKey?.trim()
  if (documentKey && event.documentKey === documentKey) return true
  return event.elementId === activeSession.elementId
}

/** Регистрация UI-колбэков открытого редактора (один активный сеанс). */
export function registerR7EditorOfficeCallback(
  handlers: R7EditorOfficeCallbackHandlers,
): () => void {
  activeSession = handlers
  return () => {
    if (activeSession === handlers) {
      activeSession = null
    }
  }
}

/** Вызов из `onOfficeCallbackStatus` FILEATTACHMENTS после promote mark/clear. */
export function dispatchR7EditorOfficeCallback(event: OfficeCallbackStatusEvent): void {
  if (!matchesActiveSession(event)) return

  activeSession?.onEditorEvent?.('r7-callback-status', {
    syncState: event.syncState,
    dsStatus: event.dsStatus,
  })

  if (isOfficeCallbackTerminalError(event)) {
    activeSession?.onSaveError?.('')
    return
  }
  if (isOfficeCallbackSaveReady(event)) {
    activeSession?.onStaged?.({
      storageUri: event.storageUri ?? '',
      changed: true,
    })
  }
}
